import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import ModuleCard from '../../components/common/ModuleCard';
import VideoLightbox from '../../components/common/VideoLightbox';
import { youtubeThumb } from '../../lib/moduleVideos';
import { RESEARCHER_OVERVIEW } from '../../lib/mockResearcher';
import { PlayCircle } from 'lucide-react';

const RESEARCH_IN_ACTION_IMAGE = 'https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/eyelm26o_PXL_20260518_084640731.MP.webp';
const FIELD_TRIAL_VIDEO_ID = 'TFD2Yv4fzbM';

export default function ResearcherOverview() {
  const d = RESEARCHER_OVERVIEW;
  const [fieldTrialOpen, setFieldTrialOpen] = useState(false);
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="researcher-overview-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
              <h1 className="mt-3 text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to={d.hero.ctaPrimary.to} data-testid="researcher-hero-explore">{d.hero.ctaPrimary.label}</GradientButton>
                <GradientButton to={d.hero.ctaSecondary.to} variant="outline" data-testid="researcher-hero-collab">{d.hero.ctaSecondary.label}</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-white p-3 shadow-soft">
              <div className="aspect-video overflow-hidden rounded-2xl bg-black">
                <img
                  src={RESEARCH_IN_ACTION_IMAGE}
                  alt={d.hero.imageLabel}
                  loading="lazy"
                  className="h-full w-full object-cover"
                  data-testid="researcher-hero-image"
                />
              </div>
              <div className="px-3 pb-3 pt-3 text-center text-[11px] font-bold tracking-widest text-purple">{d.hero.imageLabel}</div>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-4">
            {d.facts.map((f, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-4 shadow-soft">
                <div className="text-[10px] font-bold tracking-widest text-purple">{f.k}</div>
                <div className="mt-1 text-[13.5px] font-semibold text-navy">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESEARCH PURPOSE */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.purpose.eyebrow} title={d.purpose.title} desc={d.purpose.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.purpose.items.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRENT RESEARCH */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.current.eyebrow} title={d.current.title} desc={d.current.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr]">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[11px] font-bold tracking-widest text-purple">{d.current.studyEyebrow}</div>
              <h3 className="mt-2 text-[20px] font-extrabold text-navy">{d.current.studyTitle}</h3>
              <p className="mt-3 text-[13.5px] leading-relaxed text-ink-500">{d.current.studyDesc}</p>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {d.current.facts.map((f, i) => (
                  <div key={i} className="rounded-xl bg-canvas-100 px-3 py-2">
                    <div className="text-[9.5px] font-bold tracking-widest text-purple">{f.k}</div>
                    <div className="mt-0.5 text-[12.5px] font-semibold text-navy">{f.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white overflow-hidden shadow-soft">
              <button
                type="button"
                onClick={() => setFieldTrialOpen(true)}
                data-testid="researcher-field-trial-play"
                aria-label="Play field trial video"
                className="group relative block w-full aspect-video overflow-hidden focus:outline-none focus:ring-4 focus:ring-purple/30"
              >
                <img
                  src={youtubeThumb(FIELD_TRIAL_VIDEO_ID, 'maxresdefault')}
                  alt={d.current.mediaEyebrow}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-black/35 group-hover:bg-black/25 transition-colors" />
                <span className="absolute inset-0 grid place-items-center">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-white/95 shadow-soft group-hover:scale-105 transition-transform">
                    <PlayCircle className="h-7 w-7 text-navy" />
                  </span>
                </span>
              </button>
              <div className="p-4 text-[11px] font-bold tracking-widest text-purple">{d.current.mediaEyebrow}</div>
            </div>
          </div>
        </div>
      </section>

      {/* RESEARCH APPROACH */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.approach.eyebrow} title={d.approach.title} desc={d.approach.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.approach.steps.map((s) => (
              <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className="flex items-center gap-3">
                  <IconBadge icon={s.icon} color={s.color} size="lg" />
                  <StepChip n={s.n} color={s.color} />
                </div>
                <h3 className="mt-4 text-[15px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COLLABORATION AREAS */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.collabAreas.eyebrow} title={d.collabAreas.title} desc={d.collabAreas.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.collabAreas.items.map((c, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={c.icon} color={c.color} size="lg" />
                <h3 className="mt-4 text-[15.5px] font-bold text-navy">{c.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RESEARCH INTEGRITY */}
      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.integrity.eyebrow} title={d.integrity.title} desc={d.integrity.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.integrity.principles.map((p, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={p.icon} color={p.color} size="lg" />
                <h3 className="mt-4 text-[14.5px] font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULE PORTFOLIO */}
      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.portfolio.eyebrow} title={d.portfolio.title} desc={d.portfolio.desc} />
          <div className="mt-6 text-[11px] font-bold tracking-widest text-purple">AVAILABLE NOW</div>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {d.portfolio.available.map((m, i) => (
              <ModuleCard key={i} subject={m.subject} status={m.status} title={m.title} desc={m.subject} duration="35–45 min" lang="EN / HI / AS" />
            ))}
          </div>
          <div className="mt-10 text-[11px] font-bold tracking-widest text-amber-700">IN DEVELOPMENT / UPCOMING</div>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {d.portfolio.upcoming.map((m, i) => (
              <div key={i} className="flex items-center justify-between rounded-xl border border-canvas-200 bg-white px-4 py-3 shadow-soft">
                <div>
                  <div className="text-[13px] font-bold text-navy">{m.title}</div>
                  <div className="text-[11.5px] text-ink-500">{m.subject}</div>
                </div>
                <span className="rounded-full bg-canvas-200 px-2 py-0.5 text-[10px] font-bold text-ink-600">UPCOMING</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} />
        </div>
      </section>

      {fieldTrialOpen && (
        <VideoLightbox
          videoId={FIELD_TRIAL_VIDEO_ID}
          title="Gyan Dhara — Field evaluation underway"
          onClose={() => setFieldTrialOpen(false)}
        />
      )}
    </PageShell>
  );
}
